import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Database } from 'lucide-react';
import { Button } from '../components/ui/button';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#D1FAE5]">
      {/* Header */}
      <div className="bg-[#064E3B] text-white py-6">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold">Privacy Policy</h1>
          <p className="text-gray-300 mt-2">Last updated: June 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
          
          <div className="flex items-center gap-3 mb-8">
            <Shield className="h-8 w-8 text-[#064E3B]" />
            <h2 className="text-2xl font-bold text-[#064E3B]">Your Privacy Matters</h2>
          </div>

          <div className="space-y-8 text-gray-700">
            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">1. Information We Collect</h3>
              <p className="mb-3">We collect information you provide directly to us, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Phone number for account registration and WhatsApp communication</li>
                <li>Name and profile information</li>
                <li>Financial transaction data you submit via WhatsApp, our web app or mobile apps</li>
                <li>Bill, mileage, invoice and expense information</li>
                <li>Receipt images and photos you capture or upload (via your device camera or files)</li>
                <li>Subscription and purchase records (plan, status and transaction identifiers; we do not store full card numbers)</li>
                <li>Device push notification token (to deliver reminders and alerts)</li>
                <li>Device information and usage data</li>
              </ul>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <Lock className="h-5 w-5 text-[#84CC16]" />
                <h3 className="text-xl font-semibold text-[#064E3B]">2. How We Use Your Information</h3>
              </div>
              <p className="mb-3">We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide, maintain, and improve our services</li>
                <li>Process and categorize your financial transactions and read receipt images using AI</li>
                <li>Send you WhatsApp messages and push notifications (reminders, alerts and account notices)</li>
                <li>Process subscriptions and purchases</li>
                <li>Generate financial reports and insights</li>
                <li>Respond to your comments and questions</li>
                <li>Protect against fraudulent or illegal activity</li>
              </ul>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <Database className="h-5 w-5 text-[#84CC16]" />
                <h3 className="text-xl font-semibold text-[#064E3B]">3. Data Storage & Security</h3>
              </div>
              <p className="mb-3">
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>All data is encrypted in transit and at rest</li>
                <li>Access to personal data is strictly limited</li>
                <li>Regular security audits and updates</li>
                <li>Secure cloud infrastructure with data redundancy</li>
              </ul>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <Eye className="h-5 w-5 text-[#84CC16]" />
                <h3 className="text-xl font-semibold text-[#064E3B]">4. Information Sharing</h3>
              </div>
              <p className="mb-3">
                We do not sell, trade, or rent your personal information. To operate MoneyFeed we share limited data with trusted service providers (sub-processors) who process it only on our instructions and under confidentiality obligations:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>AI providers (OpenAI, Google):</strong> your message text and receipt images are processed to extract and categorize transactions. This data is not used to train their models.</li>
                <li><strong>Twilio:</strong> to send and receive WhatsApp messages and verification codes.</li>
                <li><strong>Apple (App Store) and RevenueCat:</strong> to process and validate in-app subscriptions on iOS.</li>
                <li><strong>Stripe:</strong> to process web and Android subscription payments.</li>
                <li><strong>Apple Push Notification service:</strong> to deliver push notifications to your device.</li>
                <li><strong>Cloud hosting and storage providers:</strong> to securely store your data and receipt images.</li>
              </ul>
              <p className="mt-3">
                We may also disclose information with your consent, to comply with legal obligations, or to protect our rights and safety.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">5. Your Rights</h3>
              <p className="mb-3">You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Access &amp; export your data</strong> — download a complete archive (transactions, mileage, bills and receipt images) anytime from Settings → Export Data → "Download all (ZIP)".</li>
                <li><strong>Correct inaccurate data</strong> — edit or delete individual transactions, bills and records in the app.</li>
                <li><strong>Delete your account and all data</strong> — from Settings, or at <a href="/delete-account" className="text-[#064E3B] underline">moneyfeed.io/delete-account</a> (no login required).</li>
                <li><strong>Opt out</strong> of marketing communications (reply STOP on WhatsApp).</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">6. Data Retention</h3>
              <p className="mb-3">
                We retain your data for as long as your account remains active — we do <strong>not</strong> auto-delete or expire your records. This ensures your full financial and tax history stays available to you and meets IRS record-keeping needs.
              </p>
              <p className="mb-3">
                <strong>IRS guidance (US):</strong> the IRS generally recommends keeping tax records for at least <strong>3 years</strong>, and up to <strong>6–7 years</strong> in certain situations (e.g., underreported income, or claims for bad debt / worthless securities). Because your MoneyFeed data is retained for the life of your account, it remains available throughout this period.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Active data is kept indefinitely while your account is open (no forced expiry).</li>
                <li>When you delete an individual transaction, bill or receipt, it is removed from our active database immediately.</li>
                <li>When you delete your <strong>account</strong>, all associated data is permanently erased from our active systems immediately; residual copies in encrypted backups are purged within <strong>30 days</strong>.</li>
                <li><strong>Tip:</strong> before deleting your account, use Settings → Export Data → "Download all (ZIP)" to keep a copy for your IRS records.</li>
                <li>Where required by law, we may retain limited information for the minimum period legally mandated.</li>
              </ul>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <Lock className="h-5 w-5 text-[#84CC16]" />
                <h3 className="text-xl font-semibold text-[#064E3B]">7. WhatsApp Communications &amp; Opt-Out</h3>
              </div>
              <p className="mb-3">
                Because MoneyFeed operates over WhatsApp, by registering your phone number you consent to receive service and promotional messages from us on WhatsApp (reminders, updates, onboarding, and feature announcements). Message frequency varies and message/data rates may apply.
              </p>
              <p>
                You may withdraw consent and opt out at any time by replying <strong>STOP</strong> to any WhatsApp message from us, or by contacting us. Reply <strong>START</strong> to re-subscribe. You can also opt out of marketing communications without affecting essential service messages. Opting out does not delete your account or stored data.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">8. Contact Us</h3>
              <p>
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                <p><strong>Email:</strong> feed@maxefeed.com</p>
                <p><strong>Phone:</strong> +1 (832) 688-6863</p>
                <p><strong>Address:</strong> 5900 Balcones Drive 7274, Austin, TX 78731</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
